'use client';

import { useEffect, useState } from 'react';
import BottomNav from '@/app/components/BottomNav';
import { Check, Save } from 'lucide-react';
import { RateStorage } from '@/lib/local-storage';
import Link from 'next/link';

interface DirectionConfig {
  buyerRate: number;
  sellerRate: number;
}

interface TraderSettings {
  KWD_GHS: DirectionConfig;
  GHS_KWD: DirectionConfig;
}

interface Exchanger {
  id: number;
  name: string;
  logo: string;
  charge: string;
  rate: string;
  created_at?: string;
  updated_at?: string;
}

export default function ProfilePage() {
  const [activeMainTab, setActiveMainTab] = useState<'rates' | 'exchangers'>('rates');
  const [activeRateTab, setActiveRateTab] = useState<'KWD_GHS' | 'GHS_KWD'>('KWD_GHS');
  const [traders, setTraders] = useState<TraderSettings>({
    KWD_GHS: {
      buyerRate: 0,
      sellerRate: 0,
    },
    GHS_KWD: {
      buyerRate: 0,
      sellerRate: 0,
    },
  });

  const [exchangers, setExchangers] = useState<Exchanger[]>([]);
  const [editingExchangerId, setEditingExchangerId] = useState<number | null>(null);
  const [editingData, setEditingData] = useState<Partial<Exchanger>>({});
  const [saved, setSaved] = useState(false);
  const [savedExchanger, setSavedExchanger] = useState(false);

  // Load from database on component mount, fallback to local storage
  useEffect(() => {
    const loadRates = async () => {
      try {
        // Try to fetch from database first
        const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/rates/get`);
        if (response.ok) {
          const data = await response.json();
          const rates = data.data || [];
          
          const kwd_ghs = rates.find((r: any) => r.direction === 'KWD_GHS');
          const ghs_kwd = rates.find((r: any) => r.direction === 'GHS_KWD');
          
          setTraders({
            KWD_GHS: {
              buyerRate: kwd_ghs?.buyer_rate || 0,
              sellerRate: kwd_ghs?.seller_rate || 0,
            },
            GHS_KWD: {
              buyerRate: ghs_kwd?.buyer_rate || 0,
              sellerRate: ghs_kwd?.seller_rate || 0,
            },
          });
          return;
        }
      } catch (error) {
        console.log('Failed to load rates from database, falling back to local storage:', error);
      }

      // Fallback to local storage
      try {
        const kwd_ghs_rate = await RateStorage.getRate('KWD_GHS');
        const ghs_kwd_rate = await RateStorage.getRate('GHS_KWD');

        setTraders({
          KWD_GHS: {
            buyerRate: kwd_ghs_rate?.buyer_rate || 0,
            sellerRate: kwd_ghs_rate?.seller_rate || 0,
          },
          GHS_KWD: {
            buyerRate: ghs_kwd_rate?.buyer_rate || 0,
            sellerRate: ghs_kwd_rate?.seller_rate || 0,
          },
        });
      } catch (error) {
        console.error('Failed to load rates:', error);
      }
    };

    loadRates();
  }, []);

  // Load exchangers from database
  useEffect(() => {
    const loadExchangers = async () => {
      try {
        const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/exchangers`);
        if (response.ok) {
          const data = await response.json();
          setExchangers(data.data || []);
        }
      } catch (error) {
        console.error('Failed to load exchangers:', error);
      }
    };

    loadExchangers();
  }, []);

  const handleEditExchanger = (exchanger: Exchanger) => {
    setEditingExchangerId(exchanger.id);
    setEditingData({ ...exchanger });
  };

  const handleSaveExchanger = async (id: number) => {
    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/exchangers`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id,
          name: editingData.name || '',
          logo: editingData.logo || '',
          charge: editingData.charge || '',
          rate: editingData.rate || '',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setExchangers(exchangers.map(e => e.id === id ? data.data : e));
        setEditingExchangerId(null);
        setSavedExchanger(true);
        setTimeout(() => setSavedExchanger(false), 2000);
      }
    } catch (error) {
      console.error('Failed to save exchanger:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingExchangerId(null);
    setEditingData({});
  };

  const handleInputChange = (field: keyof DirectionConfig, value: string) => {
    const numValue = parseFloat(value) || 0;
    setTraders(prev => ({
      ...prev,
      [activeRateTab]: {
        ...prev[activeRateTab],
        [field]: numValue,
      },
    }));
  };

  const handleSave = async () => {
    try {
      // Save only the active tab's direction to database
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/rates/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          direction: activeRateTab,
          buyerRate: traders[activeRateTab].buyerRate,
          sellerRate: traders[activeRateTab].sellerRate,
        }),
      });

      if (response.ok) {
        // Also save to local storage for offline support
        await RateStorage.saveRate(
          activeRateTab,
          traders[activeRateTab].buyerRate,
          traders[activeRateTab].sellerRate
        );

        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } else {
        console.error('Failed to save rates to database');
      }
    } catch (error) {
      console.error('Failed to save rates:', error);
    }
  };

  const currentConfig = traders[activeRateTab];

  return (
    <div className="bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 min-h-screen overflow-y-scroll pt-6 pb-40 px-4 sm:px-6 w-screen overflow-x-hidden">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1">Settings</h1>
        <p className="text-slate-400 text-xs sm:text-sm">Manage your preferences</p>
      </div>

      {/* Main Tab Navigation */}
      <div className="flex gap-2 sm:gap-3 mb-6 sm:mb-8">
        <button
          onClick={() => setActiveMainTab('rates')}
          className={`flex-1 py-2 sm:py-3 px-3 sm:px-4 rounded-lg text-sm sm:text-base font-semibold transition-all duration-200 ${
            activeMainTab === 'rates'
              ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
              : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
          }`}
        >
          Rates
        </button>
        <button
          onClick={() => setActiveMainTab('exchangers')}
          className={`flex-1 py-2 sm:py-3 px-3 sm:px-4 rounded-lg text-sm sm:text-base font-semibold transition-all duration-200 ${
            activeMainTab === 'exchangers'
              ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
              : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
          }`}
        >
          Exchangers
        </button>
      </div>

      {/* Rates Tab Content */}
      {activeMainTab === 'rates' && (
        <>
          {/* Rate Direction Tabs */}
          <div className="flex gap-2 sm:gap-3 mb-6 sm:mb-8">
            <button
              onClick={() => setActiveRateTab('KWD_GHS')}
              className={`flex-1 py-2 sm:py-3 px-3 sm:px-4 rounded-lg text-sm sm:text-base font-semibold transition-all duration-200 ${
                activeRateTab === 'KWD_GHS'
                  ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/30'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              KWD → GHS
            </button>
            <button
              onClick={() => setActiveRateTab('GHS_KWD')}
              className={`flex-1 py-2 sm:py-3 px-3 sm:px-4 rounded-lg text-sm sm:text-base font-semibold transition-all duration-200 ${
                activeRateTab === 'GHS_KWD'
                  ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/30'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              GHS → KWD
            </button>
          </div>

          {/* Content Container */}
          <div className="space-y-4 sm:space-y-6">
            {/* Buyer Rate Section */}
            <div className="bg-slate-800 rounded-2xl p-4 sm:p-6 border border-slate-700">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                  activeRateTab === 'KWD_GHS' ? 'bg-blue-600' : 'bg-amber-600'
                }`}>
                  {activeRateTab === 'KWD_GHS' ? 'KW' : 'GH'}
                </div>
                <div>
                  <h2 className="text-white font-semibold">Buyer Rate</h2>
                  <p className="text-xs text-slate-400">
                    {activeRateTab === 'KWD_GHS' ? 'KWD per USDT' : 'GHS per USDT'}
                  </p>
                </div>
              </div>
              <input
                type="number"
                value={currentConfig.buyerRate || ''}
                onChange={(e) => handleInputChange('buyerRate', e.target.value)}
                placeholder={activeRateTab === 'KWD_GHS' ? '0.3050' : '14.50'}
                step={activeRateTab === 'KWD_GHS' ? '0.0001' : '0.01'}
                className="w-full bg-slate-700/50 border border-slate-600 rounded-lg p-4 text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500 focus:bg-slate-700 transition-all text-lg font-semibold"
              />
            </div>

            {/* Seller Rate Section */}
            <div className="bg-slate-800 rounded-2xl p-4 sm:p-6 border border-slate-700">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                  activeRateTab === 'KWD_GHS' ? 'bg-amber-600' : 'bg-blue-600'
                }`}>
                  {activeRateTab === 'KWD_GHS' ? 'GH' : 'KW'}
                </div>
                <div>
                  <h2 className="text-white font-semibold">Seller Rate</h2>
                  <p className="text-xs text-slate-400">
                    {activeRateTab === 'KWD_GHS' ? 'GHS per USDT' : 'KWD per USDT'}
                  </p>
                </div>
              </div>
              <input
                type="number"
                value={currentConfig.sellerRate || ''}
                onChange={(e) => handleInputChange('sellerRate', e.target.value)}
                placeholder={activeRateTab === 'KWD_GHS' ? '14.45' : '0.3045'}
                step={activeRateTab === 'KWD_GHS' ? '0.01' : '0.0001'}
                className="w-full bg-slate-700/50 border border-slate-600 rounded-lg p-4 text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500 focus:bg-slate-700 transition-all text-lg font-semibold"
              />
            </div>

            {/* Save Button */}
            <button
              onClick={handleSave}
              className={`w-full py-3 sm:py-4 rounded-lg font-semibold text-sm sm:text-base flex items-center justify-center gap-2 transition-all duration-300 mt-6 sm:mt-8 ${
                saved
                  ? 'bg-emerald-600 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/30'
              }`}
            >
              {saved ? (
                <>
                  <Check size={20} />
                  <span>Saved!</span>
                </>
              ) : (
                <>
                  <Save size={20} />
                  <span>Save Rates</span>
                </>
              )}
            </button>
          </div>
        </>
      )}

      {/* Exchangers Tab Content */}
      {activeMainTab === 'exchangers' && (
        <div className="space-y-4 sm:space-y-6">
          {exchangers
            .filter((e) => e.name !== 'REMIT')
            .map((exchanger) => (
              <div key={exchanger.id} className="bg-slate-800 rounded-2xl p-4 sm:p-6 border border-slate-700">
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="text-4xl">
                      {exchanger.logo.startsWith('http') ? (
                        <img src={exchanger.logo} alt={exchanger.name} className="w-10 h-10 object-contain" />
                      ) : (
                        exchanger.logo
                      )}
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-base sm:text-lg">{exchanger.name}</h3>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-400 mb-2">Charge</label>
                      <input
                        type="text"
                        value={editingExchangerId === exchanger.id ? (editingData.charge || '') : (exchanger.charge || '')}
                        onChange={(e) => setEditingExchangerId(exchanger.id) || setEditingData({ ...editingData, charge: e.target.value })}
                        onFocus={() => {
                          if (editingExchangerId !== exchanger.id) {
                            handleEditExchanger(exchanger);
                          }
                        }}
                        className="w-full bg-slate-700/50 border border-slate-600 rounded-lg p-3 text-white text-sm focus:outline-none focus:border-blue-500"
                        placeholder="e.g., 0.5 KWD"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-2">Rate</label>
                      <input
                        type="text"
                        value={editingExchangerId === exchanger.id ? (editingData.rate || '') : (exchanger.rate || '')}
                        onChange={(e) => setEditingExchangerId(exchanger.id) || setEditingData({ ...editingData, rate: e.target.value })}
                        onFocus={() => {
                          if (editingExchangerId !== exchanger.id) {
                            handleEditExchanger(exchanger);
                          }
                        }}
                        className="w-full bg-slate-700/50 border border-slate-600 rounded-lg p-3 text-white text-sm focus:outline-none focus:border-blue-500"
                        placeholder="e.g., 0.3060"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}

          {/* Save Button */}
          {editingExchangerId && (
            <button
              onClick={() => handleSaveExchanger(editingExchangerId)}
              className={`w-full py-3 sm:py-4 rounded-lg font-semibold text-sm sm:text-base flex items-center justify-center gap-2 transition-all duration-300 mt-6 sm:mt-8 ${
                savedExchanger
                  ? 'bg-emerald-600 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/30'
              }`}
            >
              {savedExchanger ? (
                <>
                  <Check size={20} />
                  <span>Saved!</span>
                </>
              ) : (
                <>
                  <Save size={20} />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          )}
        </div>
      )}

      <BottomNav />
    </div>
  );
}
